import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "@/components/ui/dialog";
import {
  Select, SelectTrigger, SelectValue, SelectContent, SelectItem,
} from "@/components/ui/select";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { UsersRound, UserPlus, Mail, RotateCcw, Trash2, Shield } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import api, { formatApiError } from "@/lib/api";
import { toast } from "sonner";

export default function Team() {
  const { user } = useAuth();
  const navigate = useNavigate();

  const [users, setUsers] = useState([]);
  const [invites, setInvites] = useState([]);
  const [loading, setLoading] = useState(true);

  const [inviteOpen, setInviteOpen] = useState(false);
  const [inviteForm, setInviteForm] = useState({ email: "", name: "", role: "dispatcher" });
  const [inviting, setInviting] = useState(false);

  const [confirmDelete, setConfirmDelete] = useState(null); // {type:'user'|'invite', id, label}

  useEffect(() => {
    if (user && user.role !== "admin") {
      toast.error("Team management is admin-only");
      navigate("/settings", { replace: true });
    }
  }, [user, navigate]);

  const load = async () => {
    try {
      setLoading(true);
      const [u, i] = await Promise.all([api.get("/admin/users"), api.get("/admin/invites")]);
      setUsers(u.data);
      setInvites(i.data);
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const submitInvite = async (e) => {
    e.preventDefault();
    setInviting(true);
    try {
      const res = await api.post("/admin/invites", inviteForm);
      const link = res?.data?.invite_link;
      toast.success(`Invite sent to ${inviteForm.email}`, {
        description: link ? "Or copy the link below to share manually." : undefined,
      });
      if (link) {
        try { await navigator.clipboard.writeText(link); } catch (_e) { /* noop */ }
        toast.info("Invite link copied to clipboard", { description: link });
      }
      setInviteOpen(false);
      setInviteForm({ email: "", name: "", role: "dispatcher" });
      load();
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setInviting(false);
    }
  };

  const resendInvite = async (id) => {
    try {
      const res = await api.post(`/admin/invites/${id}/resend`);
      const link = res?.data?.invite_link;
      toast.success("Invite resent");
      if (link) {
        try { await navigator.clipboard.writeText(link); } catch (_e) { /* noop */ }
        toast.info("New invite link copied to clipboard", { description: link });
      }
      load();
    } catch (err) {
      toast.error(formatApiError(err));
    }
  };

  const doDelete = async () => {
    if (!confirmDelete) return;
    try {
      const url = confirmDelete.type === "user"
        ? `/admin/users/${confirmDelete.id}`
        : `/admin/invites/${confirmDelete.id}`;
      await api.delete(url);
      toast.success(confirmDelete.type === "user" ? "User removed" : "Invite revoked");
      setConfirmDelete(null);
      load();
    } catch (err) {
      toast.error(formatApiError(err));
    }
  };

  if (user && user.role !== "admin") return null;

  return (
    <div className="space-y-6 max-w-4xl">
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div>
          <h1 className="font-heading text-3xl sm:text-4xl font-extrabold text-slate-900">Team</h1>
          <p className="text-sm text-slate-500 mt-1">
            Invite team members and manage access.
          </p>
        </div>
        <Button
          onClick={() => setInviteOpen(true)}
          className="bg-blue-700 hover:bg-blue-800 gap-2"
          data-testid="invite-user-btn"
        >
          <UserPlus className="w-4 h-4" />
          Invite user
        </Button>
      </div>

      <Card className="border-slate-200" data-testid="users-card">
        <CardHeader className="pb-3">
          <CardTitle className="font-heading text-lg flex items-center gap-2">
            <UsersRound className="w-4 h-4 text-blue-700" />
            Team members ({users.length})
          </CardTitle>
        </CardHeader>
        <CardContent className="p-0">
          {loading && <p className="p-6 text-sm text-slate-500">Loading…</p>}
          {!loading && users.length === 0 && (
            <p className="p-6 text-sm text-slate-500">No users yet.</p>
          )}
          <div className="divide-y divide-slate-100">
            {users.map((u) => (
              <div key={u.id} className="flex items-center justify-between px-6 py-3 gap-4" data-testid={`user-row-${u.email}`}>
                <div className="min-w-0">
                  <div className="text-sm font-medium text-slate-900 truncate">{u.name}</div>
                  <div className="text-xs text-slate-500 truncate">{u.email}</div>
                </div>
                <div className="flex items-center gap-3">
                  <span className={`text-[11px] uppercase tracking-wider px-2 py-0.5 rounded-full border ${
                    u.role === "admin"
                      ? "bg-blue-50 text-blue-800 border-blue-200"
                      : "bg-slate-50 text-slate-700 border-slate-200"
                  }`}>
                    {u.role === "admin" ? (
                      <span className="inline-flex items-center gap-1"><Shield className="w-3 h-3" /> Admin</span>
                    ) : u.role}
                  </span>
                  {u.id !== user?.id && (
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => setConfirmDelete({ type: "user", id: u.id, label: u.email })}
                      className="text-rose-700 hover:text-rose-900 hover:bg-rose-50"
                      data-testid={`remove-user-${u.email}`}
                    >
                      <Trash2 className="w-4 h-4" />
                    </Button>
                  )}
                  {u.id === user?.id && (
                    <span className="text-[11px] uppercase tracking-wider text-slate-400 pr-2">You</span>
                  )}
                </div>
              </div>
            ))}
          </div>
        </CardContent>
      </Card>

      <Card className="border-slate-200" data-testid="invites-card">
        <CardHeader className="pb-3">
          <CardTitle className="font-heading text-lg flex items-center gap-2">
            <Mail className="w-4 h-4 text-blue-700" />
            Invitations
            <span className="ml-1 text-sm font-normal text-slate-500">
              ({invites.filter((i) => !i.used_at && new Date(i.expires_at) >= new Date()).length} pending)
            </span>
          </CardTitle>
        </CardHeader>
        <CardContent className="p-0">
          {!loading && invites.length === 0 && (
            <p className="p-6 text-sm text-slate-500">No invitations sent yet.</p>
          )}
          <div className="divide-y divide-slate-100">
            {invites.map((inv) => {
              const used = !!inv.used_at;
              const expired = new Date(inv.expires_at) < new Date();
              return (
                <div key={inv.id} className="flex items-center justify-between px-6 py-3 gap-4" data-testid={`invite-row-${inv.email}`}>
                  <div className="min-w-0">
                    <div className="text-sm font-medium text-slate-900 truncate">{inv.name} <span className="text-slate-400 font-normal">·</span> <span className="text-slate-600 font-normal">{inv.email}</span></div>
                    <div className="text-xs text-slate-500">
                      Role: {inv.role} · Expires {new Date(inv.expires_at).toLocaleDateString()}
                    </div>
                  </div>
                  <div className="flex items-center gap-2">
                    <span className={`text-[11px] uppercase tracking-wider px-2 py-0.5 rounded-full border ${
                      used ? "bg-emerald-50 text-emerald-800 border-emerald-200"
                        : expired ? "bg-rose-50 text-rose-800 border-rose-200"
                        : "bg-amber-50 text-amber-800 border-amber-200"
                    }`}>
                      {used ? "Accepted" : expired ? "Expired" : "Pending"}
                    </span>
                    {!used && (
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => resendInvite(inv.id)}
                        title="Resend invitation email"
                        data-testid={`resend-invite-${inv.email}`}
                      >
                        <RotateCcw className="w-4 h-4" />
                      </Button>
                    )}
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => setConfirmDelete({ type: "invite", id: inv.id, label: inv.email })}
                      className="text-rose-700 hover:text-rose-900 hover:bg-rose-50"
                      data-testid={`revoke-invite-${inv.email}`}
                    >
                      <Trash2 className="w-4 h-4" />
                    </Button>
                  </div>
                </div>
              );
            })}
          </div>
        </CardContent>
      </Card>

      {/* Invite dialog */}
      <Dialog open={inviteOpen} onOpenChange={setInviteOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Invite a team member</DialogTitle>
          </DialogHeader>
          <form onSubmit={submitInvite} className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="invite-name">Name *</Label>
              <Input
                id="invite-name"
                value={inviteForm.name}
                onChange={(e) => setInviteForm({ ...inviteForm, name: e.target.value })}
                required
                data-testid="invite-name"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="invite-email">Email *</Label>
              <Input
                id="invite-email"
                type="email"
                value={inviteForm.email}
                onChange={(e) => setInviteForm({ ...inviteForm, email: e.target.value })}
                required
                data-testid="invite-email"
              />
            </div>
            <div className="space-y-2">
              <Label>Role *</Label>
              <Select value={inviteForm.role} onValueChange={(v) => setInviteForm({ ...inviteForm, role: v })}>
                <SelectTrigger data-testid="invite-role"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="dispatcher">Dispatcher — CRUD customers &amp; tickets, no delete</SelectItem>
                  <SelectItem value="admin">Admin — Full access</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <DialogFooter>
              <Button type="button" variant="ghost" onClick={() => setInviteOpen(false)}>
                Cancel
              </Button>
              <Button type="submit" disabled={inviting} className="bg-blue-700 hover:bg-blue-800" data-testid="send-invite-btn">
                {inviting ? "Sending…" : "Send invitation"}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* Confirm delete */}
      <AlertDialog open={!!confirmDelete} onOpenChange={(o) => !o && setConfirmDelete(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>
              {confirmDelete?.type === "user" ? "Remove team member?" : "Revoke invitation?"}
            </AlertDialogTitle>
            <AlertDialogDescription>
              {confirmDelete?.type === "user"
                ? `${confirmDelete?.label} will no longer be able to log in. This can't be undone.`
                : `${confirmDelete?.label} won't be able to accept this invitation anymore.`}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={doDelete} className="bg-rose-700 hover:bg-rose-800" data-testid="confirm-delete-btn">
              Remove
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
